import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Truck, 
  Clock, 
  Package, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  FileText,
  Printer,
  AlertCircle,
  ArrowRight,
  Plus,
  Eye
} from 'lucide-react';
import GatePassPrint from '../components/GatePassPrint';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GatePassSecurityPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingGatePasses, setPendingGatePasses] = useState([]);
  const [todayOutLog, setTodayOutLog] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [draftGatePasses, setDraftGatePasses] = useState([]);  // New: Draft gate passes from "Return Later"
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Mark Out Dialog
  const [markOutDialog, setMarkOutDialog] = useState(false);
  const [selectedGatePass, setSelectedGatePass] = useState(null);
  const [markOutData, setMarkOutData] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_contact: '',
    remarks: ''
  });
  const [markingOut, setMarkingOut] = useState(false);
  
  // View Details Dialog
  const [viewDialog, setViewDialog] = useState(false);
  const [viewGatePass, setViewGatePass] = useState(null);
  
  // Print
  const printRef = useRef(null);
  const [printGatePass, setPrintGatePass] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gate-passes/metadata/categories`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [getAuthHeader]);

  const fetchPendingGatePasses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/gate-passes/pending-out`;
      if (selectedCategory !== 'all') {
        url += `?category=${selectedCategory}`;
      }
      const res = await fetch(url, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingGatePasses(data);
      }
    } catch (err) {
      console.error('Failed to fetch pending gate passes:', err);
    }
  }, [getAuthHeader, selectedCategory]);

  const fetchTodayOutLog = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gate-passes/today-out`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setTodayOutLog(data);
      }
    } catch (err) {
      console.error('Failed to fetch today out log:', err);
    }
  }, [getAuthHeader]);

  const fetchPendingReturns = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gate-passes/pending-returns`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingReturns(data);
      }
    } catch (err) {
      console.error('Failed to fetch pending returns:', err);
    }
  }, [getAuthHeader]);

  const fetchDraftGatePasses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/gate-passes?status=draft`;
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      const res = await fetch(url, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setDraftGatePasses(data);
      }
    } catch (err) {
      console.error('Failed to fetch draft gate passes:', err);
    }
  }, [getAuthHeader, selectedCategory]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gate-passes/summary`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [getAuthHeader]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchCategories(),
      fetchPendingGatePasses(),
      fetchTodayOutLog(),
      fetchPendingReturns(),
      fetchDraftGatePasses(),
      fetchSummary()
    ]);
    setLoading(false);
  }, [fetchCategories, fetchPendingGatePasses, fetchTodayOutLog, fetchPendingReturns, fetchDraftGatePasses, fetchSummary]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    fetchPendingGatePasses();
  }, [selectedCategory, fetchPendingGatePasses]);

  const openMarkOutDialog = (gatePass) => {
    setSelectedGatePass(gatePass);
    setMarkOutData({
      vehicle_number: gatePass.vehicle_number || '',
      driver_name: gatePass.driver_name || '',
      driver_contact: gatePass.driver_contact || '',
      remarks: ''
    });
    setMarkOutDialog(true);
  };

  const handleMarkOut = async () => {
    if (!markOutData.vehicle_number.trim()) {
      toast.error('Vehicle number is required');
      return;
    }

    setMarkingOut(true);
    try {
      const res = await fetch(`${API_URL}/api/gate-passes/${selectedGatePass.id}/mark-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(markOutData)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setMarkOutDialog(false);
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to mark gate pass as OUT');
      }
    } catch (err) {
      toast.error('Failed to mark gate pass as OUT');
    } finally {
      setMarkingOut(false);
    }
  };

  const openViewDialog = (gatePass) => {
    setViewGatePass(gatePass);
    setViewDialog(true);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryBadge = (category, label) => {
    const colors = {
      sales_dispatch: 'bg-blue-100 text-blue-800',
      purchase_return: 'bg-orange-100 text-orange-800',
      inter_unit_transfer: 'bg-purple-100 text-purple-800',
      sample_outward: 'bg-green-100 text-green-800',
      job_work_ojc: 'bg-yellow-100 text-yellow-800',
      repair_amc: 'bg-red-100 text-red-800',
      scrap_disposal: 'bg-gray-100 text-gray-800',
      exhibition_demo: 'bg-pink-100 text-pink-800',
      loan_rental: 'bg-indigo-100 text-indigo-800',
      personal: 'bg-teal-100 text-teal-800',
    };
    return (
      <Badge className={colors[category] || 'bg-gray-100 text-gray-800'}>
        {label}
      </Badge>
    );
  };

  const getSourceTypeLabel = (sourceType) => {
    const labels = {
      dispatch_plan: 'Dispatch Plan',
      ojc_dispatch: 'OJC Dispatch',
      store_receipt: 'Store Receipt (Return)',
      delivery_challan: 'Delivery Challan',
      inter_unit_transfer: 'Inter-Unit Transfer',
      manual: 'Manual Entry'
    };
    return labels[sourceType] || sourceType || 'Manual Entry';
  };

  const handlePrintGatePass = useReactToPrint({
    contentRef: printRef,
    documentTitle: printGatePass ? `GatePass-${printGatePass.gate_pass_number}` : 'GatePass',
    onAfterPrint: () => setPrintGatePass(null)
  });

  const handlePrint = (gatePass) => {
    setPrintGatePass(gatePass);
    setTimeout(() => {
      handlePrintGatePass();
    }, 100);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="gate-pass-security-page">
      {/* Hidden Print Component */}
      <div style={{ display: 'none' }}>
        <GatePassPrint ref={printRef} gatePass={printGatePass} copyType="ORIGINAL" />
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Gate Pass - Security</h1>
          <p className="text-gray-500">Manage outward goods movements</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/store/gate-pass/create')} data-testid="create-gate-pass-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Gate Pass
          </Button>
          <Button onClick={fetchAllData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Out</p>
                <p className="text-2xl font-bold">{summary?.by_status?.pending_out || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileText className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Drafts</p>
                <p className="text-2xl font-bold text-orange-600">{draftGatePasses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today's Out</p>
                <p className="text-2xl font-bold">{todayOutLog.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Returns</p>
                <p className="text-2xl font-bold">{summary?.pending_returns || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Overdue Returns</p>
                <p className="text-2xl font-bold text-red-600">{summary?.overdue_returns || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="pending-tab">
            Pending Out ({pendingGatePasses.length})
          </TabsTrigger>
          <TabsTrigger value="drafts" data-testid="drafts-tab" className="text-orange-600">
            Drafts ({draftGatePasses.length})
          </TabsTrigger>
          <TabsTrigger value="today" data-testid="today-tab">
            Today's Log ({todayOutLog.length})
          </TabsTrigger>
          <TabsTrigger value="returns" data-testid="returns-tab">
            Pending Returns ({pendingReturns.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Out Tab */}
        <TabsContent value="pending" className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : pendingGatePasses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No gate passes pending for verification</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingGatePasses.map(gp => (
                <Card key={gp.id} className="hover:shadow-md transition-shadow" data-testid={`pending-gp-${gp.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-bold text-lg">{gp.gate_pass_number}</span>
                          {getCategoryBadge(gp.category, gp.category_label)}
                          {gp.requires_return && (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              Returnable
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Party:</span>{' '}
                            <span className="font-medium">{gp.party_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Items:</span>{' '}
                            <span className="font-medium">{gp.item_count} items</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Source:</span>{' '}
                            <span className="font-medium">{gp.source_number || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Vehicle:</span>{' '}
                            <span className="font-medium">{gp.vehicle_number || 'Not set'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openViewDialog(gp)}
                          data-testid={`view-btn-${gp.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Draft Gate Passes Tab - From "Return Later" Store Receipts */}
        <TabsContent value="drafts" className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-orange-800">
              <strong>Draft Gate Passes:</strong> These are auto-created from Store Receipts with "Return Later" rejected items. 
              Once the supplier pickup is coordinated, change status to "Pending Out" for Security verification.
            </p>
          </div>
          
          {draftGatePasses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="mx-auto h-12 w-12 mb-4 opacity-40" />
                <p>No draft gate passes</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {draftGatePasses.map(gp => (
                <Card key={gp.id} className="border-l-4 border-l-orange-400">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {gp.gate_pass_number}
                          <Badge variant="outline" className="bg-orange-50 text-orange-700">DRAFT</Badge>
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {gp.category_label} • Source: {gp.source_number}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setViewGatePass(gp); setViewDialog(true); }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Party:</span>
                        <p className="font-medium">{gp.party_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Items:</span>
                        <p className="font-medium">{gp.items?.length || 0} item(s)</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Created:</span>
                        <p className="font-medium">{new Date(gp.created_at).toLocaleDateString('en-GB')}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Created By:</span>
                        <p className="font-medium">{gp.created_by_name}</p>
                      </div>
                    </div>
                    {gp.remarks && (
                      <p className="text-sm text-muted-foreground mt-2 italic">{gp.remarks}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Today's Out Log Tab */}
        <TabsContent value="today">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Today's Out Log</CardTitle>
              <Button variant="outline" size="sm" onClick={() => toast.info('Print report coming soon')}>
                <Printer className="h-4 w-4 mr-2" />
                Print Report
              </Button>
            </CardHeader>
            <CardContent>
              {todayOutLog.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No gate passes marked out today
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Gate Pass No.</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Party</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Verified By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {todayOutLog.map(gp => (
                      <TableRow key={gp.id} data-testid={`today-log-${gp.id}`}>
                        <TableCell className="font-medium">{formatTime(gp.out_at)}</TableCell>
                        <TableCell>{gp.gate_pass_number}</TableCell>
                        <TableCell>{getCategoryBadge(gp.category, gp.category_label)}</TableCell>
                        <TableCell>{gp.party_name}</TableCell>
                        <TableCell>{gp.vehicle_number}</TableCell>
                        <TableCell>{gp.out_by_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Returns Tab */}
        <TabsContent value="returns">
          <Card>
            <CardHeader>
              <CardTitle>Pending Returns</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingReturns.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No items pending return
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gate Pass</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Party</TableHead>
                      <TableHead>Out Date</TableHead>
                      <TableHead>Expected Return</TableHead>
                      <TableHead>Days Since</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingReturns.map(gp => (
                      <TableRow 
                        key={gp.id} 
                        className={gp.is_overdue ? 'bg-red-50' : ''}
                        data-testid={`return-${gp.id}`}
                      >
                        <TableCell className="font-medium">{gp.gate_pass_number}</TableCell>
                        <TableCell>{getCategoryBadge(gp.category, gp.category_label)}</TableCell>
                        <TableCell>{gp.party_name}</TableCell>
                        <TableCell>{formatDateTime(gp.out_at)}</TableCell>
                        <TableCell>{gp.expected_return_date || '-'}</TableCell>
                        <TableCell>
                          <span className={gp.days_since_out > 7 ? 'text-red-600 font-bold' : ''}>
                            {gp.days_since_out} days
                          </span>
                        </TableCell>
                        <TableCell>
                          {gp.is_overdue ? (
                            <Badge variant="destructive">Overdue</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mark Out Dialog */}
      <Dialog open={markOutDialog} onOpenChange={setMarkOutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Gate Pass as OUT</DialogTitle>
          </DialogHeader>
          
          {selectedGatePass && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-bold">{selectedGatePass.gate_pass_number}</p>
                <p className="text-sm text-gray-600">
                  {selectedGatePass.category_label} • {selectedGatePass.party_name}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedGatePass.item_count} items
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="vehicle_number">Vehicle Number *</Label>
                  <Input
                    id="vehicle_number"
                    value={markOutData.vehicle_number}
                    onChange={e => setMarkOutData(prev => ({ ...prev, vehicle_number: e.target.value.toUpperCase() }))}
                    placeholder="MH04AB1234"
                    data-testid="vehicle-number-input"
                  />
                </div>

                <div>
                  <Label htmlFor="driver_name">Driver Name</Label>
                  <Input
                    id="driver_name"
                    value={markOutData.driver_name}
                    onChange={e => setMarkOutData(prev => ({ ...prev, driver_name: e.target.value }))}
                    placeholder="Driver name"
                  />
                </div>

                <div>
                  <Label htmlFor="driver_contact">Driver Contact</Label>
                  <Input
                    id="driver_contact"
                    value={markOutData.driver_contact}
                    onChange={e => setMarkOutData(prev => ({ ...prev, driver_contact: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>

                <div>
                  <Label htmlFor="remarks">Remarks</Label>
                  <Input
                    id="remarks"
                    value={markOutData.remarks}
                    onChange={e => setMarkOutData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Any observations"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkOutDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkOut} 
              disabled={markingOut}
              data-testid="confirm-mark-out-btn"
            >
              {markingOut ? 'Processing...' : 'Confirm OUT'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gate Pass Details</DialogTitle>
          </DialogHeader>
          
          {viewGatePass && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-lg">{viewGatePass.gate_pass_number}</p>
                  {getCategoryBadge(viewGatePass.category, viewGatePass.category_label)}
                </div>
                <Badge variant={viewGatePass.status === 'pending_out' ? 'outline' : 'default'}>
                  {viewGatePass.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>

              {/* Source Document Info - Highlighted */}
              {viewGatePass.source_type && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-blue-600 font-medium">Document Type</p>
                      <p className="font-bold">{getSourceTypeLabel(viewGatePass.source_type)}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 font-medium">Document No.</p>
                      <p className="font-bold">{viewGatePass.source_number || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Party</p>
                  <p className="font-medium">{viewGatePass.party_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium">{viewGatePass.location_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Vehicle</p>
                  <p className="font-medium">{viewGatePass.vehicle_number || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Packages</p>
                  <p className="font-medium">{viewGatePass.num_packages || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created By</p>
                  <p className="font-medium">{viewGatePass.created_by_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created At</p>
                  <p className="font-medium">{formatDateTime(viewGatePass.created_at)}</p>
                </div>
                {viewGatePass.requires_return && (
                  <div>
                    <p className="text-gray-500">Expected Return</p>
                    <p className="font-medium">{viewGatePass.expected_return_date || 'Not set'}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-gray-500 mb-2">Items ({viewGatePass.items?.length || 0})</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Pkgs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewGatePass.items?.map((item, idx) => (
                      <TableRow key={item.id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{item.item_name}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell className="text-right">{item.num_packages || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {viewGatePass.remarks && (
                <div>
                  <p className="text-gray-500">Remarks</p>
                  <p className="text-sm">{viewGatePass.remarks}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => handlePrint(viewGatePass)}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            {viewGatePass?.status === 'pending_out' && (
              <Button 
                onClick={() => {
                  setViewDialog(false);
                  openMarkOutDialog(viewGatePass);
                }}
                data-testid="view-mark-out-btn"
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Mark OUT
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
};

export default GatePassSecurityPage;
